/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Brushaway } from '../../../ui/molecules/Brushaway/Brushaway';
import { BrushawayHeader } from '../../../ui/molecules/Brushaway/BrushawayHeader';
import { BrushawayBody } from '../../../ui/molecules/Brushaway/BrushawayBody';
import { ChatWidgetConversation } from '../components/ChatWidgetConversation';
import { ChatWidgetDrawer } from '../components/ChatWidgetDrawer';
import { MESSAGES_KEY_PREFIX } from '../hooks/useChatMessages';
import { useMockSSE } from './MockSSEProvider';
import { ChatWidgetContextProvider, useChatWidgetContext } from '../contexts/ChatWidgetContext';
import '../AIChatWidgetStyles.scss';
import { extractTextFromContent } from '../helpers/historyHelpers';
import { PromptLibraryModal } from '../components/PromptLibraryModal';
const noop = () => undefined;
function renderTrigger({ onClick }) {
    return (React.createElement("button", { type: "button", className: "ai-chat-widget__trigger", onClick: onClick, "aria-label": "Open AI Assistant" },
        React.createElement("i", { className: "fa-light fa-message-bot" })));
}
const INITIAL_STREAM = {
    responseId: null,
    conversationId: null,
    turnNumber: null,
    agent: null,
    blocks: [],
    streamingBlockId: null,
    streamingBlockType: null,
    streamingContent: '',
    isStreaming: false,
    responseState: 'idle',
    suggestedActions: [],
    durationMs: 0,
    feedbackId: null,
    pendingApproval: null,
    progressPhase: null,
    progressThoughtKey: null,
    progressMessage: null,
    progressDetail: null,
    progressAgent: null,
    pendingNudge: null
};
const DEFAULT_AGENT = {
    name: 'office',
    display_name: 'Assistant',
    color: '#585870',
    icon: 'fa-light fa-message-bot',
    short_description: ''
};
function MockChatWidgetInner({ conversations = [], sseSequence, userMessage = 'Ask me anything', autoPlay = false, autoPlayDelayMs = 500, userName, preSeededMessages = [], connectionState, reconnectAttempts = 0 }) {
    const { replaySequence, stopSequence } = useMockSSE();
    const [streamingMessageId, setStreamingMessageId] = useState(null);
    const [activeConversationId] = useState('conv-mock');
    const [hasSent, setHasSent] = useState(false);
    const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
    const { setRetryPrompt } = useChatWidgetContext();
    const queryClient = useQueryClient();
    const streamQueryKey = useMemo(() => ['ai-chat', 'stream', activeConversationId], [activeConversationId]);
    const messagesQueryKey = useMemo(() => [...MESSAGES_KEY_PREFIX, activeConversationId], [activeConversationId]);
    const [streamState, setStreamState] = useState(INITIAL_STREAM);
    // ---------------------------------------------------------------------------
    // Cache helpers — mirror useChatMessages helpers for the mock
    // ---------------------------------------------------------------------------
    const appendMessage = useCallback((message) => {
        queryClient.setQueryData(messagesQueryKey, (old) => {
            if (!old) {
                return {
                    pages: [{ messages: [message], next_cursor: undefined }],
                    pageParams: [undefined]
                };
            }
            const pages = old.pages.map((page, i) => (i === 0 ? Object.assign(Object.assign({}, page), { messages: [...page.messages, message] }) : page));
            return Object.assign(Object.assign({}, old), { pages });
        });
    }, [queryClient, messagesQueryKey]);
    // Seed pre-seeded messages into cache on mount
    const [seeded, setSeeded] = useState(false);
    useEffect(() => {
        if (!seeded && preSeededMessages.length > 0) {
            queryClient.setQueryData(messagesQueryKey, {
                pages: [{ messages: preSeededMessages, next_cursor: undefined }],
                pageParams: [undefined]
            });
            setSeeded(true);
        }
    }, [seeded, preSeededMessages, queryClient, messagesQueryKey]);
    // Force re-render when cache changes so messages derived value stays fresh
    const [cacheVersion, setCacheVersion] = useState(0);
    useEffect(() => {
        const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            // Update stream state from SSE query
            const data = queryClient.getQueryData(streamQueryKey);
            if (data) {
                setStreamState(data);
            }
            // Bump version so messages memo recalculates
            setCacheVersion((n) => n + 1);
        });
        return unsubscribe;
    }, [queryClient, streamQueryKey]);
    // Read messages from the React Query cache — recalculates on cacheVersion bump
    const messages = useMemo(() => {
        const data = queryClient.getQueryData(messagesQueryKey);
        if (!data)
            return [];
        return data.pages.flatMap((page) => page.messages);
        // eslint-disable-next-line no-unused-vars
    }, [queryClient, messagesQueryKey, cacheVersion]);
    useEffect(() => {
        var _a;
        if (streamState.isStreaming && !streamingMessageId && streamState.responseId) {
            setStreamingMessageId(streamState.responseId);
        }
        if (!streamState.isStreaming && streamingMessageId && streamState.blocks.length > 0) {
            const agentResponseText = streamState.blocks
                .filter((b) => b.type === 'markdown')
                .map((b) => b.content)
                .join('\n\n');
            const agentMsg = {
                id: streamingMessageId,
                role: 'agent',
                content: '',
                agent: streamState.agent || undefined,
                envelope: {
                    response_id: streamState.responseId || '',
                    conversation_id: activeConversationId,
                    tenant_id: 'mock-tenant',
                    turn_number: (_a = streamState.turnNumber) !== null && _a !== void 0 ? _a : 0,
                    agent: streamState.agent || DEFAULT_AGENT,
                    blocks: streamState.blocks,
                    suggested_actions: streamState.suggestedActions,
                    feedback_id: streamState.feedbackId || '',
                    cost: { model_used: '', input_tokens: 0, output_tokens: 0, estimated_cost_usd: 0, tool_calls_count: 0 },
                    duration_ms: streamState.durationMs,
                    created_at: new Date().toISOString(),
                    agent_response: agentResponseText,
                    citations: [],
                    deep_links: []
                },
                created_at: new Date().toISOString()
            };
            appendMessage(agentMsg);
            setStreamingMessageId(null);
        }
    }, [
        streamState.isStreaming,
        streamState.responseId,
        streamingMessageId,
        streamState,
        activeConversationId,
        appendMessage
    ]);
    const liveAgentMessage = useMemo(() => {
        var _a;
        if (!streamingMessageId || !streamState.isStreaming)
            return null;
        const liveBlocks = [...streamState.blocks];
        if (streamState.streamingContent && streamState.streamingBlockId) {
            const streamingBlock = {
                type: 'markdown',
                block_id: streamState.streamingBlockId,
                block_index: liveBlocks.length,
                content: streamState.streamingContent,
                streaming: true
            };
            liveBlocks.push(streamingBlock);
        }
        const envelope = {
            response_id: streamState.responseId || '',
            conversation_id: activeConversationId,
            tenant_id: 'mock-tenant',
            turn_number: (_a = streamState.turnNumber) !== null && _a !== void 0 ? _a : 0,
            agent: streamState.agent || DEFAULT_AGENT,
            blocks: liveBlocks,
            suggested_actions: [],
            feedback_id: '',
            cost: { model_used: '', input_tokens: 0, output_tokens: 0, estimated_cost_usd: 0, tool_calls_count: 0 },
            duration_ms: 0,
            created_at: new Date().toISOString(),
            agent_response: '',
            citations: [],
            deep_links: []
        };
        return {
            id: streamingMessageId,
            role: 'agent',
            content: '',
            agent: streamState.agent || undefined,
            envelope,
            created_at: new Date().toISOString()
        };
    }, [streamingMessageId, streamState, activeConversationId]);
    const handleSendMessage = useCallback((message) => {
        const userMsg = {
            id: `msg-${Date.now()}`,
            role: 'user',
            content: message,
            created_at: new Date().toISOString()
        };
        appendMessage(userMsg);
        setHasSent(true);
        replaySequence(sseSequence, activeConversationId);
    }, [replaySequence, sseSequence, activeConversationId, appendMessage]);
    const handleFeedback = useCallback(() => {
        return undefined;
    }, []);
    const handleRetry = useCallback(() => {
        const lastUserMessage = [...messages].reverse().find((msg) => msg.role === 'user');
        if (lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content) {
            const text = extractTextFromContent(lastUserMessage.content);
            setRetryPrompt(text);
        }
    }, [messages, setRetryPrompt]);
    const handleSelectPrompt = useCallback((prompt) => {
        // Stop active stream if any
        if (streamState.isStreaming || streamingMessageId) {
            stopSequence(activeConversationId);
            setStreamingMessageId(null);
        }
        // Send as new message (in mock, we stay on the same conversation)
        const userMsg = {
            id: `msg-${Date.now()}`,
            role: 'user',
            content: prompt,
            created_at: new Date().toISOString()
        };
        appendMessage(userMsg);
        setHasSent(true);
        replaySequence(sseSequence, activeConversationId);
    }, [
        streamState.isStreaming,
        streamingMessageId,
        stopSequence,
        activeConversationId,
        appendMessage,
        replaySequence,
        sseSequence
    ]);
    const handleStop = useCallback(() => {
        stopSequence(activeConversationId);
    }, [stopSequence, activeConversationId]);
    useEffect(() => {
        if (autoPlay && !hasSent) {
            const timer = setTimeout(() => {
                handleSendMessage(userMessage);
            }, autoPlayDelayMs);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [autoPlay, hasSent, handleSendMessage, userMessage, autoPlayDelayMs]);
    const handleOpenPromptLibrary = useCallback(() => {
        setIsPromptLibraryOpen(true);
    }, []);
    const handleClosePromptLibrary = useCallback(() => {
        setIsPromptLibraryOpen(false);
    }, []);
    const handleUsePrompt = useCallback((prompt) => {
        setIsPromptLibraryOpen(false);
        setRetryPrompt(prompt);
    }, [setRetryPrompt]);
    const drawer = (React.createElement(ChatWidgetDrawer, { conversations: conversations, activeConversationId: activeConversationId, isLoading: false, onSelectConversation: noop, onNewConversation: noop, onPromptLibrary: handleOpenPromptLibrary }));
    return (React.createElement(React.Fragment, null,
        React.createElement(Brushaway, { anchor: renderTrigger, minWidth: 960, drawer: drawer, drawerPosition: "left" },
            React.createElement(BrushawayHeader, { title: "AI Assistant" }),
            React.createElement(BrushawayBody, null,
                React.createElement(ChatWidgetConversation, { messages: messages, userName: userName, isStreaming: streamState.isStreaming, liveAgentMessage: liveAgentMessage, onSendMessage: handleSendMessage, onSelectPrompt: handleSelectPrompt, onFeedback: handleFeedback, onRetry: handleRetry, onStop: handleStop, connectionState: connectionState, reconnectAttempts: reconnectAttempts }))),
        React.createElement(PromptLibraryModal, { isOpen: isPromptLibraryOpen, onClose: handleClosePromptLibrary, onUsePrompt: handleUsePrompt })));
}
export function MockChatWidget({ conversations, sseSequence, userMessage, autoPlay, autoPlayDelayMs, userName, preSeededMessages, connectionState, reconnectAttempts }) {
    return (React.createElement(ChatWidgetContextProvider, null,
        React.createElement(MockChatWidgetInner, { conversations: conversations, sseSequence: sseSequence, userMessage: userMessage, autoPlay: autoPlay, autoPlayDelayMs: autoPlayDelayMs, userName: userName, preSeededMessages: preSeededMessages, connectionState: connectionState, reconnectAttempts: reconnectAttempts })));
}
